import React, {HTMLAttributes} from 'react';
import {Controller, get, useWatch} from 'react-hook-form';
import SubHeader from 'app/gakki/components/SubHeader';
import Select from 'components/Select';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {
  FormItem,
  Input,
  DatePicker,
  ErrorMessage,
  Switch,
} from 'components/form-items';
import {DateTime} from 'luxon';
import Block from './block';
import { salaryCategory } from '@gakki/api/types';

const SalaryBlock: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={`flex flex-wrap items-start gap-[15px] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

const SalaryData = (props: any) => {
  const {formProps, defaultValues, options, onBoardingDate, updateLocale, resetSubjectState} =
    props;
  const [_, setResetSubject] = resetSubjectState
  const {control, errors, formState} = formProps;

  const salaryNamePrefix = `personalPayroll`;
  const isEnable = useWatch({
    control,
    name: `${salaryNamePrefix}.enabled`,
    defaultValue: defaultValues.enabled,
  });

  const isError = (name: string) =>
    isEnable && formState.isSubmitted && get(errors, name);
  const renderErrorMessage = (name: string) =>
    isError(name) && <ErrorMessage>{get(errors, name)?.message}</ErrorMessage>;

  return (
    <div data-test-id={'salary-data'}>
      <SubHeader>
        <span style={{paddingRight: '15px'}}>個人薪資主檔</span>
        <Controller
          render={({onChange, value}) => (
            <div data-test-id={'enable-salary-data'} className="flex items-center">
              <Switch
                checked={value}
                onChange={(e: any) => {
                  onChange(e);
                  updateLocale();
                }}
              />
            </div>
          )}
          name={`${salaryNamePrefix}.enabled`}
          control={control}
          defaultValue={defaultValues.enabled}
        />
      </SubHeader>
      <Block>
        <div className={'flex'}>
          <SalaryBlock>
            <FormItem
              info={
                <div>
                  日期格式為 YYYY/MM/DD
                  <br />
                  可直接填寫，或選擇日期
                </div>
              }
              label="生效日期"
              itemName="salary_effectiveDate"
              required
              width="160px"
              data-test-id={'effective-date'}
            >
              <Controller
                render={({onChange, value}) => (
                  <DatePicker
                    dateFormat="yyyy/MM/dd"
                    minDate={transferDateObj(onBoardingDate)}
                    selected={transferDateObj(value)}
                    disabled={formState.isSubmitting || !isEnable}
                    onChange={(date: Date) => {
                      onChange(formatYYYYMMDD(date));
                      updateLocale();
                    }}
                    error={isError(`${salaryNamePrefix}.effectiveDate`)}
                  />
                )}
                name={`${salaryNamePrefix}.effectiveDate`}
                control={control}
                rules={{
                  required: isEnable ? '生效日期為必填' : false,
                  validate: {
                    isAfterOnBoardingDate: v => {
                      let onBoardingDateObj = transferDateObj(onBoardingDate);
                      let effectiveDateObj = transferDateObj(v);
                      if (isEnable && onBoardingDateObj && effectiveDateObj) {
                        return (
                          DateTime.fromJSDate(onBoardingDateObj) <=
                            DateTime.fromJSDate(effectiveDateObj) ||
                          `不可早於到職日${onBoardingDate}`
                        );
                      }
                      return true;
                    },
                  },
                }}
                defaultValue={defaultValues.effectiveDate}
              />
              {renderErrorMessage(`${salaryNamePrefix}.effectiveDate`)}
            </FormItem>
            <FormItem
              label="薪資類別"
              itemName="salary_salaryCategory"
              required
              width="100px"
              data-test-id={'salary-category'}
            >
              <Controller
                render={({onChange, value}) => (
                  <Select
                    value={salaryCategory.find(o => o.value === value)}
                    options={salaryCategory}
                    onChange={(o: {value: string}) => {
                      onChange(o.value);
                      const isSalaryCategoryChange = o.value !== value
                      if (isSalaryCategoryChange) {
                        updateLocale();
                        setResetSubject(true);
                      }
                    }}
                    isDisabled={formState.isSubmitting || !isEnable}
                  />
                )}
                name={`${salaryNamePrefix}.salaryCategory`}
                control={control}
                defaultValue={defaultValues.salaryCategory}
              />
            </FormItem>
            <FormItem
              label="薪資銀行"
              itemName="salary_bankId"
              required
              width="150px"
              data-test-id={'bank-id'}
            >
              <Controller
                render={({onChange, value}) => (
                  <Select
                    value={
                      options.banks.find((o: any) => o.bankId === value) || null
                    }
                    options={options.banks}
                    onChange={(o: any) => {
                      onChange(o.bankId);
                      updateLocale();
                    }}
                    isDisabled={formState.isSubmitting || !isEnable}
                    getOptionLabel={(o: any) => `${o.code} ${o.name}`}
                    getOptionValue={(o: any) => o.bankId}
                    error={
                      isEnable &&
                      formState.isSubmitted &&
                      get(errors, `${salaryNamePrefix}.bankId`)
                    }
                  />
                )}
                name={`${salaryNamePrefix}.bankId`}
                control={control}
                rules={{required: isEnable ? '薪資銀行為必填' : false}}
                defaultValue={defaultValues.bankId}
              />
            </FormItem>
            <FormItem
              label="分行代碼"
              itemName="salary_bankBranchCode"
              width="100px"
              data-test-id={'bank-branch-code'}
            >
              <Controller
                render={({onChange, value}) => (
                  <Input
                    value={value}
                    disabled={formState.isSubmitting || !isEnable}
                    onChange={(e: any) => {
                      onChange(e);
                      updateLocale();
                    }}
                    maxLength={10}
                    error={
                      formState.isSubmitted &&
                      get(errors, `${salaryNamePrefix}.bankBranchCode`)
                    }
                  />
                )}
                name={`${salaryNamePrefix}.bankBranchCode`}
                control={control}
                defaultValue={defaultValues.bankBranchCode}
              />
              {get(errors, `${salaryNamePrefix}.bankBranchCode`) && (
                <ErrorMessage>
                  {get(errors, `${salaryNamePrefix}.bankBranchCode`)?.message}
                </ErrorMessage>
              )}
            </FormItem>
            <FormItem
              label="薪資帳號"
              itemName="salary_salaryAccount"
              width="270px"
              data-test-id={'salary-account'}
            >
              <Controller
                render={({onChange, value}) => (
                  <Input
                    value={value}
                    disabled={formState.isSubmitting || !isEnable}
                    onChange={(e: any) => {
                      onChange(e);
                      updateLocale();
                    }}
                    maxLength={100}
                    error={
                      formState.isSubmitted &&
                      get(errors, `${salaryNamePrefix}.salaryAccount`)
                    }
                  />
                )}
                name={`${salaryNamePrefix}.salaryAccount`}
                control={control}
                defaultValue={defaultValues.salaryAccount}
              />
              {get(errors, `${salaryNamePrefix}.salaryAccount`) && (
                <ErrorMessage>
                  {get(errors, `${salaryNamePrefix}.salaryAccount`)?.message}
                </ErrorMessage>
              )}
            </FormItem>
          </SalaryBlock>
        </div>
      </Block>
    </div>
  );
};

export default SalaryData;
